import React, { useEffect, useState } from 'react';
import { Container, Row, Col, Card } from 'react-bootstrap';
import { Link, useParams } from 'react-router-dom';
import { ImArrowLeft } from 'react-icons/im';
import { blogApi } from '../../apiService/blogApis';
import SEO from "../../componets/SEO";

const BlogDetail = () => {
  const { slug, id } = useParams();
  const blogId = slug || id;
  const [blog, setBlog] = useState(null);
  const [relatedBlogs, setRelatedBlogs] = useState([]);
  const [loading, setLoading] = useState(true);

  const [error, setError] = useState(null);

  const fetchBlogData = async () => {
    try {
      setLoading(true);
      const [blogResponse, relatedResponse] = await Promise.all([
        blogApi.getBlogBySlug(blogId),
        blogApi.getRelatedBlogs(blogId, { limit: 3 })
      ]);
      setBlog(blogResponse.data);
      setRelatedBlogs(relatedResponse.data || []);

      if (blogResponse.data?._id) {
        blogApi.incrementBlogView(blogResponse.data._id).catch(console.error);
      }
    } catch (error) {
      console.error('Error fetching blog:', error);
      setError('Blog not found');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (blogId) {
      fetchBlogData()
    };
  }, [blogId]);

  if (loading) {
    return (
      <Container className="mt-5 pt-5 text-center">
        <div>Loading blog post...</div>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="mt-5 pt-5 text-center">
        <Row className="mb-4 mt-4">
          <Col>
            <Link to="/blog" className="text-decoration-none d-flex align-items-center justify-content-center" style={{ color: '#F37016' }}>
              <ImArrowLeft className="me-2" />
              <span>Back to Blog</span>
            </Link>
          </Col>
        </Row>
        <div className="py-5">
          <h2 style={{ color: '#2A3040' }}>Blog Not Found</h2>
          <p className="text-muted">The blog post you're looking for doesn't exist or has been removed.</p>
        </div>
      </Container>
    );
  }

  if (!blog) return null;
  const calculateReadingTime = (content) => {
    if (!content) return 0;
    const text = content.replace(/<[^>]*>/g, '');
    const words = text.trim().split(/\s+/).length;
    return Math.ceil(words / 225);
  };

  const readingTime = calculateReadingTime(blog?.content);
  return (
    <>
      <SEO
        title={blog?.seo?.title || blog?.title}
        description={blog?.seo?.description || blog?.excerpt || blog?.title}
        keywords={blog?.seo?.keywords || "medical billing blog, rcm blog"}
      />
      <div className="blog-detail-page" style={{ paddingTop: '120px' }}>
        <Container>
          {/* Back Button */}
          <Row className="mb-4 mt-4">
            <Col>
              <Link to="/blog" className="text-decoration-none d-flex align-items-center" style={{ color: '#F37016' }}>
                <ImArrowLeft className="me-2" />
                <span>Back to Blog</span>
              </Link>
            </Col>
          </Row>

          <Row>
            <Col lg={8}>
              {/* Blog Post Header */}
              <div className="mb-4">
                <div className="d-flex align-items-center mb-3">
                  <span
                    className="badge me-3"
                    style={{
                      background: "linear-gradient(to right, #F37016 0%, #B1510F 100%)",
                      color: "white",
                      borderRadius: "12px",
                      padding: "6px 16px",
                      fontSize: "14px"
                    }}
                  >
                    {blog.category}
                  </span>
                  <span className="text-muted text-capitalize">{readingTime + ' mins to read' || ''}</span>
                </div>

                <h1 className="mb-3" style={{ fontSize: '36px', fontWeight: '700', color: '#2A3040', lineHeight: '1.2' }}>
                  {blog.title}
                </h1>

                <div className="d-flex align-items-center text-muted mb-4">
                  <span>{new Date(blog.createdAt).toLocaleDateString()}</span>
                  {blog.viewCount && (
                    <>
                      <span className="mx-2">•</span>
                      <span>{blog.viewCount} views</span>
                    </>
                  )}
                </div>
              </div>

              {/* Featured Image */}
              <div className="mb-5">
                <img
                  src={blog.coverImage?.url || require('../../assets/flatlayImg.webp')}
                  alt={blog.coverImage?.alt || blog.title}
                  className="img-fluid w-100"
                  style={{
                    borderRadius: '20px',
                    height: '400px',
                    objectFit: 'cover'
                  }}
                />
              </div>

              {/* Blog Content */}
              <div
                className="blog-content mb-5"
                style={{
                  fontSize: '16px',
                  lineHeight: '1.8',
                  color: '#2A3040'
                }}
                dangerouslySetInnerHTML={{ __html: blog.content }}
              />

              {/* Share Section */}
              <div className="border-top pt-4 mb-5">
                <h5 className="mb-3" style={{ color: '#2A3040' }}>Share this article</h5>
                <div className="d-flex gap-3">
                  {/* LinkedIn */}
                  <a
                    href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(window.location.href)}&title=${encodeURIComponent(blog.seo?.title || blog.title)}&summary=${encodeURIComponent(blog.seo?.description || blog.excerpt)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-decoration-none d-flex align-items-center justify-content-center"
                    style={{
                      width: "45px",
                      height: "45px",
                      borderRadius: "50%",
                      background: "#0A66C2",
                    }}
                  >
                    <i
                      className="fab fa-linkedin-in"
                      style={{ color: "#fff", fontSize: "22px" }}
                    ></i>
                  </a>

                  {/* Twitter */}
                  <a
                    href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(window.location.href)}&text=${encodeURIComponent(`${blog.seo?.title || blog.title} - ${blog.seo?.description || blog.excerpt}`)}&hashtags=${blog.seo?.tags?.join(',') || blog.tags?.join(',') || ''}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-decoration-none d-flex align-items-center justify-content-center"
                    style={{
                      width: "45px",
                      height: "45px",
                      borderRadius: "50%",
                      background: "#1DA1F2",
                    }}
                  >
                    <i
                      className="fab fa-twitter"
                      style={{ color: "#fff", fontSize: "22px" }}
                    ></i>
                  </a>

                  {/* Facebook */}
                  <a
                    href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-decoration-none d-flex align-items-center justify-content-center"
                    style={{
                      width: "45px",
                      height: "45px",
                      borderRadius: "50%",
                      background: "#1877F2",
                    }}
                  >
                    <i
                      className="fab fa-facebook-f"
                      style={{ color: "#fff", fontSize: "22px" }}
                    ></i>
                  </a>

                  {/* Email */}
                  <a
                    href={`mailto:?subject=${encodeURIComponent(blog.seo?.title || blog.title)}&body=${encodeURIComponent(`Check out this article: ${blog.seo?.title || blog.title}\n\n${blog.seo?.description || blog.excerpt}\n\nRead more: ${window.location.href}`)}`}
                    className="text-decoration-none d-flex align-items-center justify-content-center"
                    style={{
                      width: "45px",
                      height: "45px",
                      borderRadius: "50%",
                      background: "#28A745",
                    }}
                  >
                    <i
                      className="fas fa-envelope"
                      style={{ color: "#fff", fontSize: "22px" }}
                    ></i>
                  </a>
                </div>
              </div>
            </Col>

            {/* Sidebar */}
            <Col lg={4}>
              <div className="sticky-top" style={{ top: '100px' }}>
                {/* Related Posts */}
                <Card className="border shadow-sm mb-4" style={{ borderRadius: '20px' }}>
                  <Card.Body className="p-4">
                    <h5 className="mb-3" style={{ color: '#2A3040', fontWeight: '600' }}>Related Articles</h5>
                    {relatedBlogs?.length > 0 ? relatedBlogs.map((post, index) => (
                      <div key={post.id || post._id} className={`mb-2 pb-0 ${index !== relatedBlogs.length - 1 ? 'border-bottom' : ''}`}>
                        <Link to={`/blog/${post.slug || post.id}`} className="text-decoration-none">
                          <div className="d-flex">
                            <img
                              src={post.coverImage?.url || post.image}
                              alt={post.coverImage?.alt || post.title}
                              className="me-3"
                              style={{
                                width: '80px',
                                height: '100%',
                                objectFit: 'cover',
                                borderRadius: '8px'
                              }}
                            />
                            <div>
                              <h6
                                className="mb-1"
                                style={{
                                  fontSize: '14px',
                                  fontWeight: '600',
                                  color: '#2A3040',
                                  lineHeight: '1.3'
                                }}
                              >
                                {post.title}
                              </h6>
                              <p
                                className="my-0 text-muted"
                                style={{ fontSize: '13px', lineHeight: 1.4 }}
                              >
                                {post?.excerpt?.length > 100 ? post?.excerpt?.slice(0, 99) + '...' : post?.excerpt}
                              </p>
                            </div>
                          </div>
                        </Link>
                      </div>
                    )) : (
                      <p className="text-muted text-center">No related articles found.</p>
                    )}
                  </Card.Body>
                </Card>

                {/* Newsletter Signup */}
                <Card className="border-0 shadow-sm" style={{ borderRadius: '20px', background: 'linear-gradient(135deg, #FCE7D0 0%, #F5D5B8 100%)' }}>
                  <Card.Body className="p-4 text-center">
                    <h5 className="mb-3" style={{ color: '#2A3040', fontWeight: '600' }}>Stay Updated</h5>
                    <p className="mb-3" style={{ fontSize: '14px', color: '#2A3040' }}>
                      Get the latest healthcare insights delivered to your inbox.
                    </p>
                    <div className="d-grid">
                      <Link to="/contact">
                        <button
                          className="btn w-100"
                          style={{
                            background: "linear-gradient(to right, #F37016 0%, #B1510F 100%)",
                            color: "white",
                            borderRadius: "12px",
                            padding: "10px 20px",
                            border: "none",
                            fontWeight: "500",
                            fontSize: "14px"
                          }}
                        >
                          Subscribe Now
                        </button>
                      </Link>
                    </div>
                  </Card.Body>
                </Card>
              </div>
            </Col>
          </Row>
        </Container>
      </div>
    </>

  );
};

export default BlogDetail;
